import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #desktopMedia = window.matchMedia('(min-width: 990px)');
  #transitionFrame;

  #prepareLayout() {
    cancelAnimationFrame(this.#transitionFrame);
    this.removeAttribute('data-transition-ready');
  }

  #enableTransitions() {
    this.#transitionFrame = requestAnimationFrame(() => {
      this.#transitionFrame = requestAnimationFrame(() => {
        if (this.isConnected) this.setAttribute('data-transition-ready', '');
        this.#transitionFrame = undefined;
      });
    });
  }

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#desktopMedia.addEventListener('change', this.#syncLayout);

    this.#syncLayout();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncLayout();
  }

  disconnectedCallback() {
    this.#prepareLayout();
    this.removeAttribute('data-initialized');
    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#desktopMedia.removeEventListener('change', this.#syncLayout);
    super.disconnectedCallback();
  }

  get slideCount() {
    return (this.refs.slides ?? []).length;
  }

  get desktopStatic() {
    return this.#desktopMedia.matches && this.dataset.desktopLayout !== 'single' && this.slideCount > 0;
  }

  #syncLayout = () => {
    this.#prepareLayout();
    this.suspend();
    const focusedSlideIndex = (this.refs.slides ?? []).findIndex((slide) => slide.contains(document.activeElement));
    this.current = focusedSlideIndex >= 0 ? focusedSlideIndex : this.current;
    this.setAttribute('aria-live', this.desktopStatic ? 'off' : 'polite');

    this.setAttribute('data-initialized', '');
    this.#enableTransitions();
    if (!document.hidden && !this.paused) this.play();
  };

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.desktopStatic || this.slideCount <= 1) return;

    this.suspend();
    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || this.matches(':focus-within') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      const hidden = !this.desktopStatic && index !== relativeIndex;
      slide.inert = hidden;
      slide.setAttribute('aria-hidden', `${hidden}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
